import type {
  AuditActionGroup,
  IntakeChannel,
  Impact,
  PortalRole,
  Priority,
  ProductType,
  ReleaseNoteItemType,
  RequestStatus,
  RequestType,
  TeamPriorityFilter,
  VersionState,
} from '@product-portal/shared';

/** French labels of the interface. Identifiers stay in English. */
export const STATUS_LABELS: Record<RequestStatus, string> = {
  submitted: 'Soumise',
  qualifying: 'Qualification en cours',
  qualified: 'Qualifiée',
  planned: 'Planifiée',
  in_progress: 'En cours',
  delivered: 'Livrée',
  // ADR-005 §3.
  refused: 'Refusée',
  duplicate: 'Doublon',
};

/** ADR-005 §2. « Livrée » in the mock-ups is `in_production`. */
export const VERSION_STATE_LABELS: Record<VersionState, string> = {
  to_plan: 'À planifier',
  in_development: 'En développement',
  in_testing: 'En recette',
  in_production: 'En production',
};

/** How a request arrived (ADR-004): « Reçue par » on screen 15, « Saisie par <PO> · <canal> » on 03. */
export const INTAKE_CHANNEL_LABELS: Record<IntakeChannel, string> = {
  portal: 'Portail',
  phone: 'Téléphone',
  email: 'Email',
  teams: 'Teams',
  meeting: 'Réunion',
  other: 'Autre',
};

export const RELEASE_NOTE_ITEM_LABELS: Record<ReleaseNoteItemType, string> = {
  feature: 'Nouveauté',
  fix: 'Correction',
};

export const TEAM_PRIORITY_FILTER_LABELS: Record<TeamPriorityFilter, string> = {
  to_prioritize: 'À prioriser',
  open: 'Ouvertes',
  qualified: 'Qualifiées',
  delivered: 'Livrées',
};

/** « Action » filter of screen 14. */
export const AUDIT_GROUP_LABELS: Record<AuditActionGroup, string> = {
  qualification: 'Qualification',
  attachment: 'Rattachement',
  team: 'Équipe',
  product: 'Produit',
  jira_mapping: 'Correspondance Jira',
  jira_sync: 'Synchronisation Jira et Monday',
  backlog: 'Backlog publié',
  release_note: 'Release note',
};

/** « 2 évolutions, 5 corrections » — computed from the items (ADR-005 §5). */
export function countsLabel(counts: { features: number; fixes: number }): string {
  const parts: string[] = [];
  if (counts.features > 0) parts.push(`${counts.features} évolution${counts.features > 1 ? 's' : ''}`);
  if (counts.fixes > 0) parts.push(`${counts.fixes} correction${counts.fixes > 1 ? 's' : ''}`);
  return parts.join(', ');
}

/** « 1,5 j » */
export function formatBusinessDays(days: number): string {
  return `${days.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} j`;
}

export const TYPE_LABELS: Record<RequestType, string> = {
  bug: 'Bug',
  evolution: 'Évolution',
  help: 'Aide / accès',
};

export const IMPACT_LABELS: Record<Impact, string> = {
  blocking: 'Impact bloquant',
  annoying: 'Impact gênant',
  minor: 'Impact mineur',
};

export const ROLE_LABELS: Record<PortalRole, string> = {
  requester: 'Demandeur',
  referent: 'Référent',
  po: 'PO',
};

/** Entra app-role value shown under the role pill (screen 11). */
export const ROLE_ENTRA_VALUES: Record<PortalRole, string> = {
  requester: 'aucun rôle',
  referent: 'product_portal.referent',
  po: 'product_portal.po',
};

/** Impact as chosen in the form of screen 02. */
export const IMPACT_FORM_LABELS: Record<Impact, string> = {
  blocking: 'Bloquant — je ne peux plus travailler',
  annoying: 'Gênant — contournement possible',
  minor: 'Mineur',
};

/** Impact in the « Informations » block of screen 03. */
export const IMPACT_SHORT_LABELS: Record<Impact, string> = {
  blocking: 'Bloquant',
  annoying: 'Gênant',
  minor: 'Mineur',
};

export const TYPE_DESCRIPTIONS: Record<RequestType, string> = {
  bug: 'Quelque chose ne fonctionne plus comme avant',
  evolution: 'Un nouveau besoin ou une amélioration',
  help: 'Une question, un accès, un accompagnement',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  critical: 'Critique',
  high: 'Haute',
  normal: 'Normale',
  low: 'Basse',
};

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  transverse: 'Produit transverse',
  media_site: 'Site média',
  project: 'Projet',
};

/** Where a request without product shows its product (ADR-003). */
export const NO_PRODUCT_LABEL = 'À attribuer';

/** Option of the « Produit concerné » list that submits without product (ADR-003). */
export const OTHER_PRODUCT_LABEL = 'Autre / je ne sais pas';
